/**
 * Export Service
 * 
 * Provides functionality to export items to Excel format.
 */

import { buildConfirmedLedenExportRows } from './conviventieService';
import { createWorkbook, rowsToSheet, downloadWorkbook } from './spreadsheet';

/**
 * Export items to an Excel file
 * 
 * @param {Array} items - Array of items to export
 * @param {string} filename - Name of the exported file (without extension)
 */
export const exportToExcel = async (items, filename = 'etappes-en-catecheses') => {
  // Define column headers and map to Dutch labels
  const headers = {
    etappe: 'Etappe',
    datum: 'Datum',
    bisdom: 'Bisdom',
    parochie: 'Parochie',
    gemeenschap: 'Gemeenschap',
    catechist: 'Catechist',
    catechistEmail: 'Catechist email',
    createdBy: 'Aangemaakt door',
    createdAt: 'Aangemaakt op',
    updatedBy: 'Bewerkt door',
    updatedAt: 'Bewerkt op'
  };

  // Format date for Excel
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFullName = (voornaam, achternaam) => {
    return [voornaam, achternaam]
      .map((part) => (part || '').trim())
      .filter(Boolean)
      .join(' ');
  };

  // Transform items to export format
  const exportData = items.map(item => ({
    [headers.etappe]: item.etappe || '',
    [headers.datum]: formatDate(item.datum),
    [headers.bisdom]: item.bisdom || '',
    [headers.parochie]: item.parochie || '',
    [headers.gemeenschap]: item.gemeenschap || '',
    [headers.catechist]: getFullName(item.catechistVoornaam, item.catechistAchternaam),
    [headers.catechistEmail]: item.catechistEmail || '',
    [headers.createdBy]: item.createdBy || '',
    [headers.createdAt]: formatDateTime(item.createdAt),
    [headers.updatedBy]: item.updatedBy || '',
    [headers.updatedAt]: formatDateTime(item.updatedAt)
  }));

  const workbook = createWorkbook();
  rowsToSheet(workbook, 'Catecheses', Object.values(headers), exportData);

  // Generate filename with current date
  const date = new Date().toISOString().split('T')[0];
  const fullFilename = `${filename}-${date}.xlsx`;

  await downloadWorkbook(workbook, fullFilename);
};

/**
 * Export gemeenschappen to an Excel file
 * 
 * @param {Array} gemeenschappen - Array of gemeenschappen to export
 * @param {string} filename - Name of the exported file (without extension)
 */
export const exportGemeenschappenToExcel = async (gemeenschappen, filename = 'gemeenschappen') => {
  const headers = {
    bisdom: 'Bisdom',
    parochie: 'Parochie',
    gemeenschap: 'Gemeenschap',
    verantwoordelijke: 'Verantwoordelijke',
    email: 'Email',
    laatsteEtappe: 'Laatste Etappe',
    laatsteEtappeAfgesloten: 'Afgesloten',
    aantalLeden: 'Aantal Leden',
    opmerkingen: 'Opmerkingen',
    createdBy: 'Aangemaakt door',
    createdAt: 'Aangemaakt op',
    updatedBy: 'Bewerkt door',
    updatedAt: 'Bewerkt op'
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFullName = (voornaam, achternaam) => {
    return [voornaam, achternaam]
      .map((part) => (part || '').trim())
      .filter(Boolean)
      .join(' ');
  };

  const exportData = gemeenschappen.map(item => ({
    [headers.bisdom]: item.bisdom || '',
    [headers.parochie]: item.parochie || '',
    [headers.gemeenschap]: item.gemeenschap || '',
    [headers.verantwoordelijke]: getFullName(item.verantwoordelijkeVoornaam, item.verantwoordelijkeAchternaam),
    [headers.email]: item.verantwoordelijkeEmail || '',
    [headers.laatsteEtappe]: item.laatsteEtappe || '',
    [headers.laatsteEtappeAfgesloten]: item.laatsteEtappeAfgesloten ? 'Ja' : 'Nee',
    [headers.aantalLeden]: item.aantalLeden ?? '',
    [headers.opmerkingen]: item.opmerkingen || '',
    [headers.createdBy]: item.createdBy || '',
    [headers.createdAt]: formatDateTime(item.createdAt),
    [headers.updatedBy]: item.updatedBy || '',
    [headers.updatedAt]: formatDateTime(item.updatedAt)
  }));

  const workbook = createWorkbook();
  rowsToSheet(workbook, 'Gemeenschappen', Object.values(headers), exportData);

  const date = new Date().toISOString().split('T')[0];
  const fullFilename = `${filename}-${date}.xlsx`;

  await downloadWorkbook(workbook, fullFilename);
};

/**
 * Export confirmed members of a conviventie to an Excel file
 *
 * @param {Object} conviventie - Conviventie document data
 * @param {string} filename - Name of the exported file (without extension)
 */
export const exportConviventieBevestigingenToExcel = async (conviventie, filename = 'conviventie-export') => {
  const rows = buildConfirmedLedenExportRows(conviventie);

  const workbook = createWorkbook();

  const headers = [
    'Bisdom',
    'Parochie',
    'GemeenschapNaam',
    'Voornaam',
    'Achternaam',
    'Status',
    'Aantal',
    'Geboortedatum',
    'Email',
    'Telefoonnummer',
    'Bijzonderheden',
    'Hotel',
    'Kamer',
    'Type Kamer',
    'Type bed',
    'Aankomst',
    'Vertrek'
  ];

  rowsToSheet(workbook, 'Conviventies', headers, rows);

  const date = new Date().toISOString().split('T')[0];
  const fullFilename = `${filename}-${date}.xlsx`;

  await downloadWorkbook(workbook, fullFilename);
};
